/**
 * Task vocabulary and the rules that are pure enough to test on their own.
 *
 * Kept out of types.ts because there is behaviour here, not just shapes: which
 * statuses exist, what counts as finished, and whether a proposed dependency
 * would deadlock the board.
 */

/**
 * Where a task is.
 *
 * `blocked` is for a human reason — waiting on a client, a decision, a
 * delivery. Being blocked by *another task* is not a status: it is derived
 * from dependencies, so it cannot drift out of step with reality the way a
 * manually-set flag does.
 */
export type TaskStatus = 'todo' | 'doing' | 'blocked' | 'review' | 'done' | 'cancelled';

export const TASK_STATUSES: readonly TaskStatus[] = [
  'todo',
  'doing',
  'blocked',
  'review',
  'done',
  'cancelled',
];

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  todo: 'To do',
  doing: 'In progress',
  blocked: 'Blocked',
  review: 'In review',
  done: 'Done',
  cancelled: 'Cancelled',
};

/** Statuses that no longer need anybody's attention. */
export const isFinished = (status: TaskStatus): boolean =>
  status === 'done' || status === 'cancelled';

export type TaskPriority = 'low' | 'normal' | 'high' | 'urgent';

export const TASK_PRIORITIES: readonly TaskPriority[] = ['low', 'normal', 'high', 'urgent'];

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

/* -------------------------------------------------------------------------- */
/* Links                                                                       */
/* -------------------------------------------------------------------------- */

export interface TaskLink {
  label: string;
  url: string;
}

/**
 * Is this safe to render as a link someone might click?
 *
 * Only http and https. A `javascript:` URL in a task link is a stored
 * cross-site scripting hole the moment it becomes an anchor, and tasks are
 * written by one person and read by everybody — exactly the shape that makes
 * it worth caring about.
 *
 * An allowlist on the literal prefix rather than a parse of the scheme: this
 * module is shared by the server and the browser and so cannot assume `URL`
 * exists, and the strict form is the safer one regardless. Browsers strip
 * control characters before parsing, so `java\nscript:` is a live scheme to a
 * parser and plainly not an http prefix here.
 */
export function isSafeUrl(url: string): boolean {
  return /^https?:\/\/[^\s]+$/i.test(url.trim());
}

/* -------------------------------------------------------------------------- */
/* Dependencies                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Would making `task` depend on `dependsOn` create a cycle?
 *
 * A cycle is a deadlock nobody can unpick from the interface: every task in
 * the loop waits for another, and none can be finished first. Cheaper to
 * refuse the edge than to detect the mess afterwards.
 *
 * `existing` maps a task to the tasks it already waits for. Walks forward from
 * the proposed dependency: if we can reach `task` again, adding the edge would
 * close a loop.
 */
export function wouldCycle(
  task: string,
  dependsOn: string,
  existing: ReadonlyMap<string, readonly string[]>,
): boolean {
  // Depending on yourself is the shortest possible cycle.
  if (task === dependsOn) return true;

  const seen = new Set<string>();
  const stack = [dependsOn];

  while (stack.length > 0) {
    const current = stack.pop();
    if (current === undefined || seen.has(current)) continue;
    seen.add(current);
    if (current === task) return true;
    for (const next of existing.get(current) ?? []) {
      if (!seen.has(next)) stack.push(next);
    }
  }
  return false;
}

/**
 * Which of a task's dependencies are still outstanding.
 *
 * A cancelled blocker counts as cleared: the work is not happening, so waiting
 * on it forever would be wrong.
 */
export function outstandingBlockers(
  dependsOn: readonly string[],
  statusOf: (id: string) => TaskStatus | undefined,
): string[] {
  return dependsOn.filter((id) => {
    const status = statusOf(id);
    return status !== undefined && !isFinished(status);
  });
}
